import { useState } from 'react';
import type { DiaSemana, FranjaDeAtencion, Servicio } from '@medchoice/shared';
import { ApiError } from '../../api/client';
import { consultorio } from '../../api/consultorio';
import { usePedido } from '../../api/usePedido';
import { soloDigitos } from '../../forms/validacion';
import {
  AvisoError, Boton, Cargando, Casilla, Entrada, EstadoVacio, FalloDeCarga, Insignia, Modal, Selector, Tarjeta,
  useAvisos,
} from '../../ui';
import { SeccionPanel } from '../../ui/layout/PaginaPanel';
import { pesos } from '../../ui/format';
import { Seccion } from './piezas';

const DURACIONES = [15, 20, 25, 30, 45, 60, 90].map((m) => ({ valor: String(m), texto: `${m} minutos` }));
const DIAS: { id: DiaSemana; texto: string }[] = [
  { id: 'lun', texto: 'Lunes' }, { id: 'mar', texto: 'Martes' }, { id: 'mie', texto: 'Miércoles' },
  { id: 'jue', texto: 'Jueves' }, { id: 'vie', texto: 'Viernes' }, { id: 'sab', texto: 'Sábado' }, { id: 'dom', texto: 'Domingo' },
];

export function Servicios() {
  const [editando, setEditando] = useState<Servicio | 'nuevo' | null>(null);
  const pedido = usePedido(() => consultorio.servicios(), []);
  return (
    <SeccionPanel titulo="Servicios y horarios" descripcion="Lo que ofrecés, cuánto dura cada consulta y cuándo atendés"
      acciones={<Boton tamano="chico" icono="plus" onClick={() => setEditando('nuevo')}>Nuevo servicio</Boton>}>
      <div className="pila config">
        <Tarjeta>
          <Seccion titulo="Mis servicios"
            descripcion="Cada servicio define su precio y su duración. La duración es la que ocupa el turno en tu agenda.">
            {pedido.estado === 'cargando' && <Cargando />}
            {pedido.estado === 'error' && <FalloDeCarga mensaje={pedido.error ?? ''} alReintentar={pedido.recargar} />}
            {pedido.datos?.length === 0 && (
              <EstadoVacio icono="list-checks" titulo="Todavía no cargaste servicios"
                accion={<Boton onClick={() => setEditando('nuevo')}>Cargar el primero</Boton>} />
            )}
            {pedido.datos && pedido.datos.length > 0 && (
              <ListaDeServicios servicios={pedido.datos} alEditar={setEditando} alCambiar={pedido.recargar} />
            )}
          </Seccion>
        </Tarjeta>
        <HorarioDeAtencion />
      </div>
      {editando && (
        <ModalServicio servicio={editando === 'nuevo' ? null : editando}
          alCerrar={() => setEditando(null)} alGuardar={pedido.recargar} />
      )}
    </SeccionPanel>
  );
}

function ListaDeServicios({ servicios, alEditar, alCambiar }: {
  servicios: Servicio[]; alEditar: (s: Servicio) => void; alCambiar: () => void;
}) {
  const { mostrarAviso } = useAvisos();
  const cambiarEstado = async (s: Servicio) => {
    try {
      if (s.activo) await consultorio.darDeBajaServicio(s.id_servicio);
      else await consultorio.editarServicio(s.id_servicio, { activo: true });
      mostrarAviso({ tono: 'exito', titulo: s.activo ? 'Servicio dado de baja' : 'Servicio reactivado' });
      alCambiar();
    } catch (e) {
      mostrarAviso({ tono: 'error', titulo: 'No se pudo cambiar el servicio', cuerpo: e instanceof ApiError ? e.message : undefined });
    }
  };
  return (
    <ul className="lista-servicios">
      {servicios.map((s) => (
        <li key={s.id_servicio} className={s.activo ? undefined : 'lista-servicios__inactivo'}>
          <div>
            <strong>{s.nombre}</strong>
            <span>{pesos(s.precio)} · {s.duracion_min} minutos{s.permite_videoconsulta ? ' · también por videoconsulta' : ''}</span>
          </div>
          {!s.activo && <Insignia tono="neutral" tamano="chico">Dado de baja</Insignia>}
          <Boton tamano="chico" variante="secundario" onClick={() => alEditar(s)}>Editar</Boton>
          <Boton tamano="chico" variante="fantasma" onClick={() => void cambiarEstado(s)}>{s.activo ? 'Dar de baja' : 'Reactivar'}</Boton>
        </li>
      ))}
    </ul>
  );
}

function ModalServicio({ servicio, alCerrar, alGuardar }: { servicio: Servicio | null; alCerrar: () => void; alGuardar: () => void }) {
  const { mostrarAviso } = useAvisos();
  const [nombre, setNombre] = useState(servicio?.nombre ?? '');
  const [precio, setPrecio] = useState(servicio ? String(servicio.precio / 100) : '');
  const [duracion, setDuracion] = useState(String(servicio?.duracion_min ?? 30));
  const [video, setVideo] = useState(servicio?.permite_videoconsulta ?? false);
  const [error, setError] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);
  const errorPrecio = precio && Number(precio) < 1000 ? 'El precio mínimo es de $1.000' : null;

  const guardar = async () => {
    setGuardando(true);
    setError(null);
    const datos = { nombre, precio: Math.round(Number(precio) * 100), duracion_min: Number(duracion), permite_videoconsulta: video };
    try {
      if (servicio) await consultorio.editarServicio(servicio.id_servicio, datos);
      else await consultorio.agregarServicio(datos);
      mostrarAviso({ tono: 'exito', titulo: servicio ? 'Servicio actualizado' : 'Servicio agregado',
        cuerpo: servicio ? 'Los turnos ya reservados mantienen su precio.' : 'Ya se puede reservar.' });
      alGuardar();
      alCerrar();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'No pudimos guardar el servicio. Probá de nuevo.');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal abierto alCerrar={alCerrar} titulo={servicio ? 'Editar servicio' : 'Nuevo servicio'}
      pie={<>
        <Boton variante="secundario" onClick={alCerrar}>Cancelar</Boton>
        <Boton disabled={nombre.trim().length < 3 || !precio || Boolean(errorPrecio)} cargando={guardando}
          onClick={() => void guardar()}>Guardar</Boton>
      </>}>
      <AvisoError>{error}</AvisoError>
      <div className="formulario">
        <Entrada etiqueta="Nombre del servicio" placeholder="Consulta dermatológica" maxLength={100} value={nombre}
          onChange={(e) => setNombre(e.target.value)} />
        <div className="formulario__fila">
          <Entrada etiqueta="Precio" placeholder="45000" inputMode="numeric" sufijo="pesos" value={precio}
            error={errorPrecio ?? undefined} onChange={(e) => setPrecio(soloDigitos(e.target.value, 8))} />
          <Selector etiqueta="Duración" value={duracion} opciones={DURACIONES} onChange={(e) => setDuracion(e.target.value)} />
        </div>
        <Casilla etiqueta="También se puede atender por videoconsulta" checked={video} onChange={(e) => setVideo(e.target.checked)} />
      </div>
    </Modal>
  );
}

function HorarioDeAtencion() {
  const pedido = usePedido(() => consultorio.horarios(), []);
  return (
    <Tarjeta>
      <Seccion titulo="Horario de atención"
        descripcion="Los pacientes reservan dentro de estas franjas, en bloques de 30 minutos. Para ausencias puntuales usá el bloqueo de horarios desde la agenda.">
        {pedido.estado === 'cargando' && <Cargando />}
        {pedido.datos && <EditorDeHorarios inicial={pedido.datos} />}
      </Seccion>
    </Tarjeta>
  );
}

type Franja = FranjaDeAtencion & { clave: number };

function EditorDeHorarios({ inicial }: { inicial: FranjaDeAtencion[] }) {
  const { mostrarAviso } = useAvisos();
  const [franjas, setFranjas] = useState<Franja[]>(() => inicial.map((f, i) => ({ ...f, clave: i })));
  const [error, setError] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);

  const cambiar = (clave: number, cambios: Partial<FranjaDeAtencion>) =>
    setFranjas((fs) => fs.map((f) => (f.clave === clave ? { ...f, ...cambios } : f)));
  const agregar = (dia: DiaSemana) =>
    setFranjas((fs) => [...fs, { dia_semana: dia, hora_inicio: '09:00', hora_fin: '13:00', clave: Date.now() }]);
  const quitar = (clave: number) => setFranjas((fs) => fs.filter((f) => f.clave !== clave));

  const guardar = async () => {
    setGuardando(true);
    setError(null);
    try {
      await consultorio.definirHorarios(franjas.map(({ clave: _, ...f }) => f));
      mostrarAviso({ tono: 'exito', titulo: 'Horario guardado', cuerpo: 'Los turnos ya reservados no cambian.' });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'No pudimos guardar el horario. Probá de nuevo.');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="formulario">
      <div className="semana-atencion">
        {DIAS.map((d) => {
          const delDia = franjas.filter((f) => f.dia_semana === d.id).sort((a, b) => a.hora_inicio.localeCompare(b.hora_inicio));
          return (
            <div key={d.id} className="semana-atencion__dia">
              <strong>{d.texto}</strong>
              <div className="semana-atencion__franjas">
                {delDia.length === 0 && <span className="nota">No atendés</span>}
                {delDia.map((f) => (
                  <div key={f.clave} className="semana-atencion__franja">
                    <input type="time" step={1800} aria-label={`${d.texto}, desde`} value={f.hora_inicio}
                      onChange={(e) => cambiar(f.clave, { hora_inicio: e.target.value })} />
                    <span>a</span>
                    <input type="time" step={1800} aria-label={`${d.texto}, hasta`} value={f.hora_fin}
                      onChange={(e) => cambiar(f.clave, { hora_fin: e.target.value })} />
                    <Boton variante="icono" icono="x" aria-label="Quitar franja" onClick={() => quitar(f.clave)} />
                  </div>
                ))}
              </div>
              <Boton tamano="chico" variante="fantasma" icono="plus" onClick={() => agregar(d.id)}>Franja</Boton>
            </div>
          );
        })}
      </div>
      <AvisoError>{error}</AvisoError>
      <div className="acciones-pie">
        <Boton cargando={guardando} onClick={() => void guardar()}>Guardar horario</Boton>
      </div>
    </div>
  );
}
